import * as React from "react";
import MUIDataTable, {
  MUIDataTableColumn,
  MUIDataTableOptions,
} from "mui-datatables";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  FormControl,
  Switch,
  TextField,
} from "@mui/material";
import axios from "../../api";
import SoftBox from "components/SoftBox";
import AddSubscriptionType from "./AddSubscriptionType";
import PageLoader from "examples/ReuseFunctions/PageLoader";
import SlideOverPanel from "components/SlideOverPanel";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import SoftTypography from "components/SoftTypography";
import { AxiosError } from "axios";

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

// ---------- TYPES ----------

interface SubscriptionTypeData {
  subscription_type_id: number;
  subscription_type_name: string;
  duration_in_days: number;
  price: number;
  asset_type_id: number;
  asset_type_name: string;
  description: string;
  is_active: number;
  include_free_service?: number;
  free_service_count?: number | null;
}

interface SelectedRow {
  dataIndex: number;
}

interface TableMeta {
  rowIndex: number;
  columnIndex: number;
  currentTableData: Array<{ index: number; data: unknown[] }>;
  tableData: SubscriptionTypeData[];
}

interface EditData {
  subscription_type_id: number;
  asset_type_id: number;
  subscription_type_name: string;
  description: string;
  price: number;
  duration_in_days: number;
  include_free_service?: number;
  free_service_count?: number | null;
}

interface AssetTypeItem {
  asset_type_id: number;
  asset_type_name: string;
}

interface ColumnMeta {
  label?: string;
  name: string;
  index: number;
}

// ---------- EXTRACTED COMPONENTS ----------

interface CheckboxHeaderProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
}

const CheckboxHeader: React.FC<CheckboxHeaderProps> = ({
  checked,
  onChange,
}) => (
  <th style={{ padding: 16, background: "#f8f9fa" }}>
    <Checkbox checked={checked} onChange={(e) => onChange(e.target.checked)} />
  </th>
);

interface CheckboxCellProps {
  checked: boolean;
  onChange: () => void;
}

const CheckboxCell: React.FC<CheckboxCellProps> = ({ checked, onChange }) => (
  <Checkbox checked={checked} onChange={onChange} />
);

interface StatusSwitchProps {
  value: number;
  row: SubscriptionTypeData;
  onStatusChange: (row: SubscriptionTypeData, isActive: boolean) => void;
}

const StatusSwitch: React.FC<StatusSwitchProps> = ({
  value,
  row,
  onStatusChange,
}) => (
  <Switch
    checked={!!value}
    onChange={(e) => onStatusChange(row, e.target.checked)}
  />
);

interface EditButtonProps {
  row: SubscriptionTypeData;
  onEdit: (row: SubscriptionTypeData) => void;
}

const EditButton: React.FC<EditButtonProps> = ({ row, onEdit }) => (
  <Button variant="outlined" onClick={() => onEdit(row)} color="primary">
    Edit
  </Button>
);

const tableOptions: MUIDataTableOptions = {
  selectableRows: "none",
  selectableRowsHeader: false,
  elevation: 0,
  tableBodyHeight: "auto",
  responsive: "standard",
};

const SubscriptionTypesList: React.FC = () => {
  const [subscriptionTypeData, setSubscriptionTypeData] = React.useState<
    SubscriptionTypeData[]
  >([]);

  const [assetTypes, setAssetTypes] = React.useState<AssetTypeItem[]>([]);
  const [assetTypeFilter, setAssetTypeFilter] =
    React.useState<AssetTypeItem | null>(null);

  const [selectedRows, setSelectedRows] = React.useState<SelectedRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [isOpen, setOpen] = React.useState(false);

  const [editData, setEditData] = React.useState<EditData | null>(null);

  const { fetchError, fetchSuccess } = useSnackbar();

  const handleEditOpen = (row: SubscriptionTypeData) => {
    const formatted: EditData = {
      subscription_type_id: row.subscription_type_id,
      asset_type_id: row.asset_type_id,
      subscription_type_name: row.subscription_type_name,
      description: row.description,
      price: row.price,
      duration_in_days: row.duration_in_days,
      include_free_service: row.include_free_service,
      free_service_count: row.free_service_count,
    };

    setEditData(formatted);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditData(null);
  };

  const handleOpen = () => {
    setOpen(true);
    setEditData(null);
  };

  const handleStatus = async (row: SubscriptionTypeData, isActive: boolean) => {
    const prevData = subscriptionTypeData;
    setSubscriptionTypeData((current) =>
      current.map((r) =>
        r.subscription_type_id === row.subscription_type_id
          ? { ...r, is_active: isActive ? 1 : 0 }
          : r
      )
    );
    try {
      const response = await axios.put(
        `/subscription-types/${row.subscription_type_id}`,
        { is_active: isActive }
      );
      await fetchData();
      fetchSuccess(response.data.message);
    } catch (error) {
      setSubscriptionTypeData(prevData);
      if (error instanceof AxiosError && error.response) {
        fetchError(
          error.response?.data?.error ?? "Failed to update status"
        );
      }
    }
  };

  const fetchData = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get("/subscription-types", {
        params: {
          filters: {
            asset_type_id: assetTypeFilter?.asset_type_id,
          },
        },
      });
      setSubscriptionTypeData(response.data.list);
    } finally {
      setLoading(false);
    }
  }, [assetTypeFilter]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchAssets = async () => {
    const res = await axios.get("/asset-types");
    setAssetTypes(res.data.list);
  };

  React.useEffect(() => {
    fetchAssets();
  }, []);

  const handleBulkStatusUpdate = async (isActive: boolean): Promise<void> => {
    try {
      const requests = selectedRows.map((row) => {
        const sub = subscriptionTypeData[row.dataIndex];
        return axios.put(`/subscription-types/${sub.subscription_type_id}`, {
          is_active: isActive,
        });
      });

      await Promise.all(requests);
      setSelectedRows([]);
      fetchData();

      fetchSuccess(
        isActive
          ? "Subscription type activated"
          : "Subscription type deactivated"
      );
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      }
    }
  };

  // ---------- CHECKBOXES ----------
  const handleCheckAllboxes = (checked: boolean) => {
    if (!subscriptionTypeData.length) return;
    setSelectedRows(
      checked ? subscriptionTypeData.map((_, idx) => ({ dataIndex: idx })) : []
    );
  };

  const handleCheckboxChange = (rowIndex: number) => {
    setSelectedRows((prev) => {
      const exists = prev.some((r) => r.dataIndex === rowIndex);
      return exists
        ? prev.filter((r) => r.dataIndex !== rowIndex)
        : [...prev, { dataIndex: rowIndex }];
    });
  };

  // Render functions for table columns
  const renderCheckboxHeader = () => {
    return (
      <CheckboxHeader
        checked={
          subscriptionTypeData.length > 0 &&
          selectedRows.length === subscriptionTypeData.length
        }
        onChange={handleCheckAllboxes}
      />
    );
  };

  const renderCheckboxCell = (dataIndex: number) => {
    return (
      <CheckboxCell
        checked={selectedRows.some((r) => r.dataIndex === dataIndex)}
        onChange={() => handleCheckboxChange(dataIndex)}
      />
    );
  };

  const renderStatusSwitch = (value: number, tableMeta: TableMeta) => {
    const row =
      tableMeta.tableData[tableMeta.currentTableData[tableMeta.rowIndex].index];
    return (
      <StatusSwitch value={value} row={row} onStatusChange={handleStatus} />
    );
  };

  const renderEditButton = (dataIndex: number) => {
    const row = subscriptionTypeData[dataIndex];
    return <EditButton row={row} onEdit={handleEditOpen} />;
  };

  const columns: MUIDataTableColumn[] = [
    {
      name: "checkbox",
      label: "",
      options: {
        filter: false,
        sort: false,
        customHeadRender: renderCheckboxHeader,
        customBodyRenderLite: renderCheckboxCell,
      },
    },

    { name: "subscription_type_name", label: "Subscription Type" },
    { name: "duration_in_days", label: "Duration (Days)" },
    { name: "asset_type_name", label: "Asset Type" },
    { name: "price", label: "Price" },

    {
      name: "is_active",
      label: "Status",
      options: {
        customBodyRender: renderStatusSwitch,
      },
    },
    {
      name: "actions",
      label: "Actions",
      options: {
        customBodyRenderLite: renderEditButton,
      },
    },
  ];

  return (
    <>
      <Card
        style={{ padding: "24px", margin: "16px auto", maxWidth: "2000px" }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            marginBottom: "16px",
            flexWrap: "wrap",
            gap: 12,
          }}
          className="manage-tickets-header"
        >
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "flex-end" }}>
            <FormControl sx={{ width: 200, minWidth: 200, flexShrink: 0 }}>
              <SoftTypography
                variant="caption"
                fontWeight="bold"
                sx={{ paddingBottom: "6px" }}
              >
                Asset Types
              </SoftTypography>
              <Autocomplete
                options={assetTypes}
                getOptionLabel={(opt) => opt.asset_type_name}
                value={assetTypeFilter}
                onChange={(_, val) => setAssetTypeFilter(val)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    sx={{
                      "& .MuiInputBase-input": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                )}
              />
            </FormControl>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              className="success-btn"
              // variant="gradient"
              color="success"
              onClick={() => {
                handleOpen();
              }}
              sx={{ marginRight: "10px", padding: 2 }}
            >
              Add Subscription Type
            </Button>
            <Button
              className="success-btn"
              onClick={() => {
                handleBulkStatusUpdate(true);
              }}
              disabled={!selectedRows.length}
              sx={{ marginRight: "10px", padding: 2 }}
            >
              Activate
            </Button>
            <Button
              className="success-btn"
              onClick={() => {
                handleBulkStatusUpdate(false);
              }}
              disabled={!selectedRows.length}
              sx={{ padding: 2 }}
            >
              InActivate
            </Button>
          </Box>
        </div>
        <SoftBox
          sx={{
            "& .MuiTableRow-root:not(:last-child)": {
              "& td": (theme: Theme) => ({
                borderBottom: `${theme.borders.borderWidth[1]} solid ${theme.borders.borderColor}`,
              }),
            },
          }}
          className="manage-tickets m-category"
        >
          {loading ? (
            <PageLoader />
          ) : (
            <ThemeProvider theme={getMuiTheme}>
              <MUIDataTable
                title="Manage Subscription Types"
                data={subscriptionTypeData}
                columns={columns}
                options={tableOptions}
              />
            </ThemeProvider>
          )}
        </SoftBox>
      </Card>
      <SlideOverPanel
        open={isOpen}
        onClose={handleClose}
        title={editData ? "Edit Subscription Type" : "Add Subscription Type"}
        width={1000}
      >
        <AddSubscriptionType
          isOpen={true}
          onClose={handleClose}
          fetchData={() => {
            handleClose();
            fetchData();
          }}
          editData={editData}
          embedded
        />
      </SlideOverPanel>
    </>
  );
};

export default SubscriptionTypesList;
